import { Navbar } from "../components/layout/Navbar";
import { Link } from "react-router-dom";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Check, ArrowLeft, Calendar, Users, TrendingUp, Award } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Program = () => {
  const { t, language } = useLanguage();

  // Translated timeline data
  const timeline = [
    {
      phase: t('program.timeline.phase1.phase'),
      title: t('program.timeline.phase1.title'),
      items: [
        t('program.timeline.phase1.items.0'),
        t('program.timeline.phase1.items.1'),
        t('program.timeline.phase1.items.2'),
      ],
    },
    {
      phase: t('program.timeline.phase2.phase'),
      title: t('program.timeline.phase2.title'),
      items: [
        t('program.timeline.phase2.items.0'),
        t('program.timeline.phase2.items.1'),
      ],
    },
    {
      phase: t('program.timeline.phase3.phase'),
      title: t('program.timeline.phase3.title'),
      items: [
        t('program.timeline.phase3.items.0'),
        t('program.timeline.phase3.items.1'),
        t('program.timeline.phase3.items.2'),
      ],
    },
  ];

  // Translated benefits data
  const benefits = [
    {
      icon: Users,
      title: t('program.benefits.funding.title'),
      description: t('program.benefits.funding.description'),
    },
    {
      icon: TrendingUp,
      title: t('program.benefits.partnership.title'),
      description: t('program.benefits.partnership.description'),
    },
    {
      icon: Calendar,
      title: t('program.benefits.training.title'),
      description: t('program.benefits.training.description'),
    },
    {
      icon: Award,
      title: t('program.benefits.certificate.title'),
      description: t('program.benefits.certificate.description'),
    },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-2 rounded-full bg-primary-foreground/20 text-sm font-semibold mb-6">
                {t('program.hero.programName')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {t('program.hero.title')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed mb-8">
                <div dangerouslySetInnerHTML={{ __html: t('program.hero.description') }} />
              </p>
              <Link to="/project-holder-register" className="inline-flex items-center justify-center gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90 px-6 py-3 rounded-md text-base font-medium transition-colors">
                {t('program.hero.registerNow')}
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Program Overview */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">{t('program.overview.title')}</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t('program.overview.description')}
              </p>
              
              <div className="bg-card rounded-2xl p-8 shadow-card mb-8">
                <h3 className="text-2xl font-bold mb-4">{t('program.overview.mainTitle')}</h3>
                <p className="text-muted-foreground mb-6">
                  {t('program.overview.mainDescription')}
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{t('program.overview.benefits.funding')}</h4>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{t('program.overview.benefits.partnership')}</h4>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{t('program.overview.benefits.training')}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Program Mechanisms */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">{t('program.mechanisms.title')}</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-card rounded-2xl p-6 shadow-card">
                  <h3 className="text-xl font-bold mb-4">{t('program.mechanisms.sales.title')}</h3>
                  <ul className="space-y-3">
                    {t('program.mechanisms.sales.items.0').split(',').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-card rounded-2xl p-6 shadow-card">
                  <h3 className="text-xl font-bold mb-4">{t('program.mechanisms.partnership.title')}</h3>
                  <ul className="space-y-3">
                    {t('program.mechanisms.partnership.items.0').split(',').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Main Objectives */}
              <div className="bg-card rounded-2xl p-8 shadow-card">
                <h2 className="text-2xl font-bold mb-6">{t('program.mechanisms.objectives.title')}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="space-y-4">
                    {t('program.mechanisms.objectives.items.0').split(',').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                  <ul className="space-y-4">
                    {t('program.mechanisms.objectives.items.3').split(',').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('program.timeline.title')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('program.timeline.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {timeline.map((month, index) => (
                <div
                  key={month.phase}
                  className="bg-card rounded-2xl p-8 shadow-card hover:shadow-elevated transition-shadow"
                >
                  <span className="inline-block px-3 py-1 rounded-full bg-hero-gradient text-primary-foreground text-sm font-semibold mb-4">
                    {month.phase}
                  </span>
                  <h3 className="text-xl font-bold mb-4">{month.title}</h3>
                  <ul className="space-y-3">
                    {month.items.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Program Value Proposition */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('program.valueProposition.title')}</h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('program.valueProposition.description')}
              </p>
            </div>

            <div className="bg-card rounded-2xl p-8 shadow-card mb-12">
              <h3 className="text-2xl font-bold mb-6 text-center">{t('program.valueProposition.mainTitle')}</h3>
              
              <div className="mb-8">
                <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.mainObjective')}</h4>
                <p className="text-muted-foreground mb-6">
                  {t('program.valueProposition.mainObjectiveDesc')}
                </p>
                
                <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.expectedResults')}</h4>
                <p className="text-muted-foreground">
                  {t('program.valueProposition.expectedResultsDesc')}
                </p>
              </div>
              
              <div className="border-t border-border pt-8">
                <h3 className="text-2xl font-bold mb-6 text-center">{t('program.valueProposition.offerTitle')}</h3>
                <p className="text-muted-foreground mb-6 text-center">
                  {t('program.valueProposition.offerDesc')}
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="text-right">
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.training.title')}</h4>
                    <p className="text-muted-foreground mb-4">
                      {t('program.valueProposition.offerItems.training.desc')}
                    </p>
                    
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.guidance.title')}</h4>
                    <p className="text-muted-foreground mb-4">
                      {t('program.valueProposition.offerItems.guidance.desc')}
                    </p>
                    
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.funding.title')}</h4>
                    <p className="text-muted-foreground">
                      {t('program.valueProposition.offerItems.funding.desc')}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.partnership.title')}</h4>
                    <p className="text-muted-foreground mb-4">
                      {t('program.valueProposition.offerItems.partnership.desc')}
                    </p>
                    
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.loan.title')}</h4>
                    <p className="text-muted-foreground mb-4">
                      {t('program.valueProposition.offerItems.loan.desc')}
                    </p>
                    
                    <h4 className="text-xl font-semibold mb-4">{t('program.valueProposition.offerItems.certificate.title')}</h4>
                    <p className="text-muted-foreground">
                      {t('program.valueProposition.offerItems.certificate.desc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-bold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('program.cta.title')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              {t('program.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/project-holder-register" className="inline-flex items-center justify-center gap-2 bg-hero-gradient text-black hover:bg-primary-foreground/90 px-6 py-3 rounded-md text-base font-medium transition-colors">
                {t('program.cta.applyNow')}
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Program;